import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router';
import { FetchStatus, FetchType } from '../../../enums';
import { Team } from '../../../models/view/team';
import { useAppActions, useAppState } from '../../../overmind';
import { TagInput } from '../../../components/inputs/tags/tag-input';
import { TagType } from '../../../enums/tag';
import { BaseSearchRequest } from '../../../models';
import { Assignee, TagAssigneeRequest, TagListItem } from '../../../models/tag';
import { PagePath } from '../../../navigation/navigation.enums';
import { FocusPanelLoaderLayer } from '../../../components/general/loading-state/loader-layers/focus-panel-loader-layer/focus-panel-loader-layer';
import { Anchor, Button, CopyButton, DropdownSelect, FieldController, PanelHeader, RequiredValidator, useMatchScreenWidth, TextField, NameValidator } from '@keplerco/core';
import { RequiredEntity } from '../../../models/view/entity';

export function ManageTeamFocusPanelCMSLayout(props: { teamSlug?: string; path: PagePath.userManagementTeams | PagePath.userManagementDepartmentTeams; onClick: () => void }): JSX.Element {
  const params = useParams<any>();
  const [tags, setTags] = useState<TagListItem[] | undefined>([]);
  const [search, setSearch] = useState('');
  const actions = useAppActions();

  const { handleSubmit, control, setValue } = useForm({ mode: 'onBlur', reValidateMode: 'onBlur' });

  const { fetchState } = useAppState();

  const [departments, setDepartments] = useState<RequiredEntity[]>();

  const isMobile = useMatchScreenWidth('mobile');

  useEffect(() => {
    async function getData() {
      actions.startLoader({ path: props.path, type: FetchType.DialogFetching });

      const departmentsData = await actions.getDepartmentKeyValuesByCompany(params.companySlug!);
      setDepartments(departmentsData?.sort((a, b) => a.name.localeCompare(b.name)) ?? []);

      if (!props.teamSlug) return;

      const teamData = await actions.getTeam({ companySlug: params.companySlug!, teamSlug: props.teamSlug });

      if (!!teamData) {
        setValue('id', teamData.id);
        setValue('teamName', teamData.teamName);
        setValue('departmentId', teamData.departmentId?.toString());
      }

      actions.stopLoader(props.path);
    }

    getData();
  }, []);

  useEffect(() => {
    if (!!departments && !props.teamSlug) actions.stopLoader(props.path);
  }, [departments]);

  async function onClickRemoveTagHandler(tagName: string) {
    const assignees: TagAssigneeRequest[] = [
      {
        entitySlug: props.teamSlug ?? '',
        tagType: TagType.Team,
      },
    ];

    await actions.removeAssignees({ tagName, assignees, companySlug: params.companySlug! });
  }
  async function fetchData() {
    const result = await actions.getAssigneeTags({ entitySlug: props.teamSlug!, tagType: TagType.Team, companySlug: params.companySlug! });
    if (result) {
      setTags(result);
    } else {
      setTags([]);
    }
  }

  useEffect(() => {
    fetchData();
  }, []);

  function getTagAssignees(): Assignee[] {
    return [
      {
        entitySlug: props.teamSlug ?? '',
        tagType: TagType.Team,
      },
    ];
  }

  async function onClickCreateTagHandler(tagName: string, assignees: Assignee[], source: string) {
    if (source === 'input') {
      tagName = search;
      assignees = [
        {
          entitySlug: props.teamSlug ?? '',
          tagType: TagType.Team,
        },
      ];
    }

    await actions.removeAssignees({ tagName, assignees, companySlug: params.companySlug! });
  }

  function onTagChange(tags: TagListItem[]) {
    setTags(tags);
  }

  async function searchUserTags(query: string): Promise<TagListItem[]> {
    setSearch(query);
    const searchTagParams: BaseSearchRequest = {
      page: 1,
      pageSize: 15,
      search: query,
      sortAscending: true,
      sortField: 0,
      companySlug: params.companySlug!,
    };
    const paginatedResult = await actions.searchTags(searchTagParams);
    if (paginatedResult && paginatedResult.tags) {
      return paginatedResult.tags;
    }
    return [];
  }

  return (
    <FocusPanelLoaderLayer path={props.path}>
      <div className="panelContent panelForm">
        {!!props.teamSlug ? (
          <PanelHeader title="Update team" type="actions" divider>
            <div className="headerActions" style={{ justifyContent: isMobile ? 'flex-start' : 'flex-end' }}>
              <CopyButton label="Team Id" value={props.teamSlug} />
            </div>
          </PanelHeader>
        ) : (
          <PanelHeader title="Create team" divider />
        )}

        <form
          id="saveTeam"
          className="panelForm"
          onSubmit={handleSubmit(async fieldValues => {
            if (fetchState[props.path].status === FetchStatus.Active) return;
            actions.startLoader({ path: props.path, type: FetchType.Sending });
            const team: Team = {
              id: parseInt(fieldValues.id),
              teamName: fieldValues.teamName,
              departmentId: parseInt(fieldValues.departmentId),
            };

            await actions.saveTeam({ team: team, companySlug: params.companySlug! });

            props.onClick();

            actions.stopLoader(props.path);
          })}
        >
          <div>
            <FieldController
              control={control}
              rules={new NameValidator()}
              name="teamName"
              render={({ field, fieldState }) => {
                return (
                  <TextField
                    {...field}
                    type="text"
                    label="Team name"
                    responsive
                    validation={{
                      dirty: fieldState.isDirty || !!fieldState.error,
                      invalid: !!fieldState.error,
                      message: fieldState.error?.message ?? 'Enter a name',
                    }}
                  />
                );
              }}
            />

            {!!departments?.length && (
              <FieldController
                control={control}
                rules={new RequiredValidator('Select a department')}
                name="departmentId"
                render={({ field, fieldState }) => (
                  <DropdownSelect
                    {...field}
                    label="Department"
                    options={departments?.map(department => ({
                      value: department.id.toString(),
                      label: department.name,
                    }))}
                    responsive
                    validation={{
                      dirty: !!fieldState.isDirty || !!fieldState.error,
                      invalid: !!fieldState.invalid || !!fieldState.error,
                      message: fieldState.error?.message ?? 'Select a department',
                    }}
                  />
                )}
              />
            )}

            {!!props.teamSlug && <TagInput getAssignees={getTagAssignees} onRemoveTag={onClickRemoveTagHandler} initialTags={tags} onCreateTag={onClickCreateTagHandler} onTagChange={onTagChange} searchTags={searchUserTags} />}
          </div>

          <footer className="panelFooter">
            <Anchor onClick={props.onClick}>Cancel</Anchor>
            <Button type="button">Submit</Button>
          </footer>
        </form>
      </div>
    </FocusPanelLoaderLayer>
  );
}
